import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import {useLinkResolver} from 'prismicio'
import type {
  ButtonDocument,
  CustomerStoryDocumentData,
  HeroWithVideoSlice,
  HeroWithVideoSliceDefaultPrimaryVideosItem,
} from 'prismicio-types'
import {useState} from 'react'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {getButtonsData, getIconName} from 'src/common/helpers'
import type {IconType} from 'src/common/Icon'
import {Icon} from 'src/common/Icon'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS_OVERRIDE} from 'src/common/paddingClasses'
import {VideoCover} from 'src/common/VideoCover'

type HeroWithVideoProps = {
  slice: HeroWithVideoSlice
  context: {pageUid?: string; buttonsData?: ButtonDocument[]}
}

type ResolvedVideo = {
  src: string
  companyName?: string
  companyLogo?: string
  companyIcon?: string
  mainImage?: string
  quoteText?: string
  quoteAuthorName?: string
  quoteAuthorRole?: string
}

const resolveVideo = (item: HeroWithVideoSliceDefaultPrimaryVideosItem): ResolvedVideo | null => {
  if (isFilled.contentRelationship(item.customer_story_document)) {
    const data = item.customer_story_document.data as CustomerStoryDocumentData | undefined

    if (!data) {
      return null
    }

    let youtubeUrl: string | null = null

    if (isFilled.link(item.youtube_url) && 'url' in item.youtube_url) {
      youtubeUrl = item.youtube_url.url ?? null
    } else if (isFilled.link(data.youtube_video_source) && 'url' in data.youtube_video_source) {
      youtubeUrl = data.youtube_video_source.url ?? null
    }

    if (!youtubeUrl) {
      return null
    }

    const firstQuote = data.quotes?.[0]

    return {
      src: youtubeUrl,
      companyName: data.company_name ?? undefined,
      companyLogo: isFilled.image(data.company_logo) ? data.company_logo.url : undefined,
      companyIcon: isFilled.image(data.company_icon) ? data.company_icon.url : undefined,
      mainImage: isFilled.image(data.image) ? data.image.url : undefined,
      quoteText: firstQuote?.quote ?? undefined,
      quoteAuthorName: firstQuote?.author_name ?? undefined,
      quoteAuthorRole: firstQuote?.author_role ?? undefined,
    }
  }

  if (isFilled.link(item.youtube_url) && 'url' in item.youtube_url) {
    return {src: item.youtube_url.url ?? ''}
  }

  return null
}

const HeroWithVideo = ({slice, context}: HeroWithVideoProps): JSX.Element => {
  const {primary} = slice

  const {linkResolver} = useLinkResolver()

  const {primaryButtonData} = getButtonsData({
    buttonsData: context.buttonsData ?? [],
    primaryButton: primary.primary_button,
  })

  const videos: ResolvedVideo[] = primary.videos.map(resolveVideo).filter((v): v is ResolvedVideo => v !== null)

  const [activeVideo, setActiveVideo] = useState(0)
  const [key, setKey] = useState(0)

  const current = videos[activeVideo]

  const handleVideoChange = (index: number) => {
    setActiveVideo(index)
    setKey((k) => k + 1)
  }

  const paddingBottomClass = PADDING_BOTTOM_CLASS_OVERRIDE[primary.padding_bottom ?? DEFAULT_PADDING] ?? ''

  const icon1 = primary.highlight_1_icon ? (getIconName(primary.highlight_1_icon) as IconType) : null

  const icon2 = primary.highlight_2_icon ? (getIconName(primary.highlight_2_icon) as IconType) : null

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`!pb-12 !pt-[122px] md:!pt-[137px] ${paddingBottomClass}`}
      backgroundColor={primary.background_color || 'Grey/100'}
      extraPaddingTop
    >
      <div className="w-full max-w-[1200px] px-5 md:px-10 lg:px-5 xl:px-0">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:gap-12">
          {/* Left — headline content */}
          <div className="flex flex-col gap-6 lg:w-[40%] lg:shrink-0">
            {isFilled.keyText(primary.eyebrow) && <p className="text-button-s text-moss-blue">{primary.eyebrow}</p>}
            {isFilled.keyText(primary.title) && (
              <h1 className="text-tg-h2 md:text-h1 font-medium text-moss-black max-md:leading-[normal] md:tracking-normal">
                {primary.title}
              </h1>
            )}
            <PrismicRichText
              components={{
                paragraph: ({children}) => (
                  <p className="text-tg-body-l max-w-[400px] text-moss-grey-550">{children}</p>
                ),
              }}
              field={primary.description}
            />

            {/* Highlights */}
            {(isFilled.keyText(primary.highlight_1_title) || isFilled.keyText(primary.highlight_2_title)) && (
              <div className="mt-2 flex w-full rounded-lg bg-moss-grey-100 p-4 md:p-6">
                {isFilled.keyText(primary.highlight_1_title) && (
                  <div className="flex flex-1 flex-col gap-1">
                    <div className="flex items-center gap-1.5">
                      {icon1 && <Icon name={icon1} className="size-5 text-neon-550 md:size-7" />}
                      <p className="text-[36px] font-normal leading-none tracking-[-0.36px] text-green-dark md:text-[48px] md:tracking-[-0.48px]">
                        {primary.highlight_1_title}
                      </p>
                    </div>
                    {isFilled.keyText(primary.highlight_1_subtext) && (
                      <p className="mt-auto pt-2 text-[11px] font-semibold uppercase leading-4 tracking-widest text-moss-grey-550">
                        {primary.highlight_1_subtext}
                      </p>
                    )}
                  </div>
                )}
                {isFilled.keyText(primary.highlight_2_title) && (
                  <div className="flex flex-1 flex-col gap-1 border-l border-moss-black/10 pl-6 md:pl-8">
                    <div className="flex items-center gap-1.5">
                      {icon2 && <Icon name={icon2} className="size-5 text-neon-550 md:size-7" />}
                      <p className="text-[36px] font-normal leading-none tracking-[-0.36px] text-green-dark md:text-[48px] md:tracking-[-0.48px]">
                        {primary.highlight_2_title}
                      </p>
                    </div>
                    {isFilled.keyText(primary.highlight_2_subtext) && (
                      <p className="mt-auto pt-2 text-[11px] font-semibold uppercase leading-4 tracking-widest text-moss-grey-550">
                        {primary.highlight_2_subtext}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {primaryButtonData && (
              <ButtonV2
                variant="primary"
                className="w-fit"
                href={linkResolver(primaryButtonData.link, 'primary-button-hero-video')}
                trackingData={{
                  mktgId: primaryButtonData.marketing_id || 'primary-button-hero-video',
                  location: 'hero-with-video',
                  dataType: 'primary-button',
                  pageName: context.pageUid || '',
                }}
              >
                {primaryButtonData.label}
              </ButtonV2>
            )}
          </div>

          {/* Right — video player */}
          {current && (
            <div className="flex flex-col gap-1 lg:w-[60%]">
              <VideoCover
                key={key}
                src={current.src}
                mainImage={current.mainImage ? {url: current.mainImage} : undefined}
                companyLogo={current.companyLogo ? {url: current.companyLogo} : undefined}
                quoteText={current.quoteText}
                quoteAuthorName={current.quoteAuthorName}
                quoteAuthorRole={current.quoteAuthorRole}
              />

              {/* Logo bar — 2x2 grid on mobile, row on desktop */}
              {videos.length > 1 && (
                <div className="flex w-full items-center justify-between gap-0">
                  {videos.map((video, index) => {
                    const isActive = index === activeVideo
                    const logoSrc = video.companyLogo || video.companyIcon

                    return (
                      <button
                        key={index}
                        onClick={() => handleVideoChange(index)}
                        className={`relative flex h-10 min-w-0 flex-1 items-center justify-center border-t-[3px] px-2 transition-all duration-200 md:h-14 md:px-6 ${
                          isActive
                            ? 'border-ui-green-900 opacity-100'
                            : 'border-moss-grey-200 opacity-40 hover:opacity-70'
                        }`}
                      >
                        {logoSrc ? (
                          <img
                            src={logoSrc}
                            alt={video.companyName || ''}
                            className="h-[18px] w-auto max-w-[70px] object-contain brightness-0 md:h-7 md:max-w-[110px]"
                          />
                        ) : (
                          <span className="text-12 font-medium text-moss-black md:text-14">
                            {video.companyName || `Video ${index + 1}`}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Container>
  )
}

export default HeroWithVideo
